<template>
  <form class="erc-vehicle-catalog-form" method="post" name="VDB">
    <div class="form-field-wrapper">
      <div class="field-group">
        <label class="" for="form-field-vehicle-type">Type:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-vehicle-type" @change="onVehicleTypeChange" v-model="form.vehicleType" name="vehicle-type">
            <option :value="null">Please select the type</option>
            <option v-for="vehicleType in vehicleTypes"
                    :key="`vehicle-type-${vehicleType.id}`"
                    :value="vehicleType.id">
              {{ vehicleType.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-brand">Brand:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-brand" @change="onBrandChange" v-model="form.brand"
                  :disabled="!form.vehicleType || !brands"
                  name="brand">
            <option :value="null">Please select the brand</option>
            <option v-for="brand in brands"
                    :key="`brand-${brand.id}`"
                    :value="brand.id">
              {{ cleanNameForDisplay(brand.name) }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-model">Model:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-model" @change="onModelChange" v-model="form.model"
                  :disabled="!form.brand || !models"
                  class="elementor-field-textual elementor-size-sm" name="model">
            <option :value="null">Please select model</option>
            <option v-for="model in models"
                    :key="`model-${model.id}`"
                    :value="model.id">
              {{ cleanNameForDisplay(model.name) }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column ">
        <label class="" for="form-field-generation">Generation:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-generation" @change="onGenerationChange" v-model="form.generation"
                  :disabled="!form.model || !generations"
                  class="elementor-field-textual elementor-size-sm" name="generation">
            <option :value="null">Please select generation</option>
            <option v-for="generation in generations"
                    :key="`generation-${generation.id}`"
                    :value="generation.id">
                    {{ generation.name }} {{ generation.year ? `(${generation.year}${generation.yearend ? ' - ' + generation.yearend : ''})` : '' }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column">
        <label class="" for="form-field-fuel">Fuel:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-fuel" @change="onFuelChange" v-model="form.fuel"
                  :disabled="!form.generation || !fuels"
                  class="elementor-field-textual elementor-size-sm" name="fuel">
            <option v-for="fuel in fuels"
                    :key="`model-${fuel.id}`"
                    :value="fuel.id">
              {{ fuel.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group elementor-column ">
        <label class="" for="form-field-engine">Engine:</label>
        <div class="form-field select-wrapper ">
          <select id="form-field-engine" @change="onEngineChange" v-model="form.engine"
                  :disabled="engines.length === 0"
                  class="elementor-field-textual elementor-size-sm" name="engine">
            <option :value="null">Please select engine</option>
            <option v-for="engine in engines"
                    :key="`engine-${engine.id}`"
                    :value="engine.id">
              {{ engine.name }}
            </option>
          </select>
        </div>
      </div>
      <div class="field-group">
        <span></span></div>
      <div class="field-group field-group__button">
        <button :disabled="!isFormValid"
                class="submit-button"
                type="submit"
                @click.prevent="submitForm">
          VIEW REMAPS
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import { API_BASE } from "@/constants/api";
import { fetchWithAuth } from '@/utils/apiUtils';
import { getFullAssetUrl } from '@/utils/urlUtils'; 

export default {
  name: 'VehicleCatalogForm',
  data() {
    return {
      vehicleTypes: [],
      brands: [],
      models: [],
      generations: [],
      fuels: [],
      engines: [],
      form: {
        vehicleType: null,
        brand: null,
        model: null,
        generation: null,
        fuel: null,
        engine: null,
      },
      isPrefilling: false,
    };
  },
  async created() {
    console.log("Form.vue: created() hook START");
    this.isPrefilling = true;
    try {
      await this.getVehicleTypes();
      await this.$router.isReady();
      await this.prefillFromUrl();
    } catch (error) {
      console.error("Error during component creation:", error);
    } finally {
      this.isPrefilling = false;
      // This final check ensures that if prefilling failed but a vehicle param exists,
      // the URL is cleared to reflect the form's actual (empty) state.
      this.$nextTick(() => {
        const currentVehicleParam = this.$route?.query?.vehicle;
        if (!this.form.engine && currentVehicleParam) {
            console.log("[Created Finally] Prefill incomplete/failed but vehicle param exists. Clearing URL param.");
            this.updateVehicleUrl();
        } else {
             console.log("[Created Finally] URL state seems consistent after prefill attempt.");
        }
      });
    }
    console.log("Form.vue: created() hook END");
  },
  computed: {
    isFormValid() {
      return !!this.form.engine;
    },

    visualGridRenderData() {
      return {
        vehicleTypes: this.vehicleTypes,
        brands: this.brands,
        models: this.models,
        generations: this.generations,
        engines: this.engines, // Will be populated after generation is selected
        form: { ...this.form }, // Pass a copy of the form state
        isPrefilling: this.isPrefilling,
      };
    }
  },
  watch: {
    visualGridRenderData: {
      handler(newData) {
        this.$emit('update-visual-grids', newData);
      },
      deep: true,
      immediate: true // Emit initial state
    }

  },

  methods: {
    cleanNameForDisplay(name) {
      if (typeof name !== 'string') return '';
      // Removes the last occurring (...) from the string
      return name.replace(/\s*\(.*\)\s*$/, '').trim();
    },
    resetFieldsFrom(level) {
        const levels = ['vehicleType', 'brand', 'model', 'generation', 'fuel', 'engine'];
        const startIndex = levels.indexOf(level);
        if (startIndex === -1) return;
        for (let i = startIndex + 1; i < levels.length; i++) {
            const field = levels[i];
            this.form[field] = null;
            if (field === 'brand') this.brands = [];
            if (field === 'model') this.models = [];
            if (field === 'generation') this.generations = [];
            if (field === 'fuel') this.fuels = [];
            if (field === 'engine') this.engines = [];
        }
        if (startIndex < levels.indexOf('engine')) { // If engine or its prerequisites are reset
            this.$emit('selection-changed-stale-remap');
        }
        console.log(`Fields reset from level: ${level}`);
    },
    async fetchData(endpointPath, params = null) {
      console.log(`Form.vue: fetchData() called for: ${endpointPath}`);
        try {
            // Use the new centralized fetch function which handles headers automatically
            return await fetchWithAuth(endpointPath, params);
        } catch (error) {
            console.error(`Error fetching ${endpointPath}:`, error);
            throw error;
        }
    },

    async onVehicleTypeChange() {
      this.resetFieldsFrom('vehicleType');
      if (this.form.vehicleType) {
          try { await this.getBrands(); } catch (e) { console.error("Failed to get brands:", e); }
      }
      if (!this.isPrefilling) {
        this.$emit('selection-changed-stale-remap');
        this.updateVehicleUrl();
      }
    },
    async onBrandChange() {
      this.resetFieldsFrom('brand');
      if (this.form.brand) {
          try { await this.getModels(); } catch (e) { console.error("Failed to get models:", e); }
      }
      if (!this.isPrefilling) {
        this.$emit('selection-changed-stale-remap');
        this.updateVehicleUrl();
      }
    },
    async onModelChange() {
      this.resetFieldsFrom('model');
      if (this.form.model) {
          try { await this.getGenerations(); } catch (e) { console.error("Failed to get generations:", e); }
      }
      if (!this.isPrefilling) {
        this.$emit('selection-changed-stale-remap');
        this.updateVehicleUrl();
      }
    },
    async onGenerationChange() {
      this.resetFieldsFrom('generation');
      if (this.form.generation) {
          try {
              await this.getFuels();
              await this.getEngines();
          } catch (e) { console.error("Failed to get fuels/engines:", e); }
      }
      if (!this.isPrefilling) {
        this.$emit('selection-changed-stale-remap');
        this.updateVehicleUrl();
      }
    },
    async onFuelChange() {
      const wasEngineSelected = !!this.form.engine;
      this.resetFieldsFrom('fuel');
      if (this.form.generation) {
          try { await this.getEngines(); } catch (e) { console.error("Failed to get engines after fuel change:", e); }
      }
      // Only clear URL if not prefilling AND an engine was previously selected and is now deselected
      if (!this.isPrefilling && wasEngineSelected && !this.form.engine) {
        this.$emit('selection-changed-stale-remap');
          this.updateVehicleUrl();
      }
    },
    async onEngineChange() {
        if (this.form.engine) { // Engine selected
            console.log('[Form Debug] Engine manually selected. Auto-submitting form.');
            this.$nextTick(() => {
                this.submitForm(); // submitForm will handle URL update
            });
        } else if (!this.isPrefilling) { // Engine deselected by user action (not during prefill)
            console.log('[Form Debug] Engine deselected by user, clearing URL.');
            this.$emit('selection-changed-stale-remap');
            this.updateVehicleUrl(); // Clear URL param
        }
        // If engine is deselected during prefill, the created().finally or prefillFromUrl().catch will handle it.
    },

    async getVehicleTypes() {
      console.log("Form.vue: getVehicleTypes() START");
      try {
        const data = await this.fetchData('vdb/vehicle-types/dropdown');
        console.log("Form.vue: getVehicleTypes() received data:", data);
        if (Array.isArray(data)) {
            // Process the icon URL here to be consistent with other data fetches
            this.vehicleTypes = data.map(type => ({
                ...type,
                // The next line is the fix. If you see the log below, it's working.
                 icon: getFullAssetUrl(type.iconUrl || type.icon)
            }));
        } else {
            console.error("Vehicle types data is not an array:", data);
            this.vehicleTypes = [];
        }
      } catch (error) {
        console.error("Error fetching vehicle types:", error);
        this.vehicleTypes = [];
      }
      console.log("Form.vue: getVehicleTypes() END");
    },
    async getBrands() {
      if (!this.form.vehicleType) { this.brands = []; return; }
      try {
        const params = new URLSearchParams({ 'vehicle-type': this.form.vehicleType });
        const data = await this.fetchData('vdb/brands/dropdown', params);
        this.brands = Array.isArray(data) ? data.map(brand => ({
          ...brand,
          logo: getFullAssetUrl(brand.logo) // Process logo URL
        })) : [];
        if (!this.isPrefilling && this.brands.length === 1) {
            console.log("Auto-selecting the only brand:", this.brands[0].name);
            this.form.brand = String(this.brands[0].id); 
            await this.onBrandChange();
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
        this.brands = [];
      }
    },
    async getModels() {
      if (!this.form.brand) { this.models = []; return; }
      try {
        const params = new URLSearchParams({ 'brand': this.form.brand, 'hide-brand': 'true' });
        const data = await this.fetchData('vdb/models/dropdown', params);
        this.models = Array.isArray(data) ? data.map(model => ({
          ...model,
          // image: getFullAssetUrl(model.image) // Process image URL
          image: model.image
              ? getFullAssetUrl(model.image)
              : model.imageUrl
                  ? getFullAssetUrl(model.imageUrl)
                  : null// Process image URL
        })) : [];
        if (!this.isPrefilling && this.models.length === 1) {
            console.log("Auto-selecting the only model:", this.models[0].name);
            this.form.model = String(this.models[0].id); 
            await this.onModelChange();
        }
      } catch (error) {
        console.error("Error fetching models:", error);
        this.models = [];
      }
    },
    async getGenerations() {
      if (!this.form.model) { this.generations = []; return; }
      try {
        const params = new URLSearchParams({ 'model': this.form.model });
        const data = await this.fetchData('vdb/generations/dropdown', params);
        this.generations = Array.isArray(data) ? data.map(gen => ({
          ...gen,
          image: getFullAssetUrl(gen.image) // Process image URL
        })) : [];
        if (!this.isPrefilling && this.generations.length === 1) {
            console.log("Auto-selecting the only generation:", this.generations[0].name);
            this.form.generation = String(this.generations[0].id);
            await this.onGenerationChange();
        }
      } catch (error) {
        console.error("Error fetching generations:", error);
        this.generations = [];
      }
    },
    async getFuels(preselecting = false) {
      if (!this.form.generation) { this.fuels = []; return; }
      try {
        const params = new URLSearchParams({ 'generation': this.form.generation });
        const data = await this.fetchData('vdb/fuel-types/dropdown', params);
        const fuelTypes = Array.isArray(data) ? data : [];
        this.fuels = [{ id: null, name: 'All fuel types' }, ...fuelTypes];

        if (!preselecting && !this.isPrefilling && fuelTypes.length === 1) {
            console.log("Auto-selecting the only fuel type:", fuelTypes[0].name);
            this.form.fuel = String(fuelTypes[0].id);
        }
        else if (!preselecting && !this.isPrefilling) {
            this.form.fuel = null;
        }
      } catch (error) {
        console.error("Error fetching fuel types:", error);
        this.fuels = [{ id: null, name: 'All fuel types' }];
      }
    },
    async getEngines() {
      if (!this.form.generation) { this.engines = []; return; }
      try {
        const params = new URLSearchParams({ 'generation': this.form.generation });
        if (this.form.fuel) {
          params.append('fuel', this.form.fuel);
        }
        const data = await this.fetchData('vdb/engines/dropdown', params);
        this.engines = Array.isArray(data) ? data : [];

        if (!this.isPrefilling && this.engines.length === 1) {
            console.log("Auto-selecting the only engine:", this.engines[0].name);
            this.form.engine = String(this.engines[0].id);
            this.$nextTick(() => {
                console.log("Auto-submitting form after engine auto-select.");
                this.submitForm();
            });
        }
      } catch (error) {
        console.error("Error fetching engines:", error);
        this.engines = [];
      }
    },

     updateVehicleUrl(vehicleParamValue = null) {
        console.log(`--- [Form Debug] updateVehicleUrl START (Value: ${vehicleParamValue}) ---`);
        if (!this.$router || !this.$route) {
            console.error("[Form Debug] Vue Router ($router/$route) is not available!");
            console.log('--- [Form Debug] updateVehicleUrl END (Router Missing) ---');
            return;
        }

        const currentQuery = { ...this.$route.query };
        const currentVehicleParamInQuery = currentQuery.vehicle;

        setTimeout(() => {
            console.log('[Form Debug] Inside setTimeout for router update.');
            let newQuery = {};
            let shouldNavigate = false;

            try {
                for (const key in currentQuery) {
                    if (key !== 'vehicle') {
                        newQuery[key] = currentQuery[key];
                    }
                }

                if (vehicleParamValue) {
                    if (vehicleParamValue !== currentVehicleParamInQuery) {
                        newQuery.vehicle = vehicleParamValue;
                        console.log("[Form Debug] Setting vehicle param:", vehicleParamValue);
                        shouldNavigate = true;
                    } else {
                        console.log('[Form Debug] New vehicle param matches current. No update needed.');
                        if (currentVehicleParamInQuery !== undefined) {
                           newQuery.vehicle = currentVehicleParamInQuery;
                        }
                    }
                } else {
                    if (currentVehicleParamInQuery !== undefined) {
                        console.log("[Form Debug] Clearing vehicle param (by not adding it to new query).");
                        shouldNavigate = true;
                    } else {
                         console.log('[Form Debug] No vehicle param to set or clear.');
                    }
                }

                if (shouldNavigate) {
                    console.log("[Form Debug] Calling window.history.replaceState with query:", JSON.parse(JSON.stringify(newQuery)));
                    try {
                        const url = new URL(window.location);
                        url.search = '';
                        for (const key in newQuery) {
                            url.searchParams.set(key, newQuery[key]);
                        }
                        window.history.replaceState(history.state, '', url.toString());
                        console.log("[Form Debug] window.history.replaceState executed.");
                    } catch(historyError) {
                        console.error("[Form Debug] window.history.replaceState FAILED:", historyError);
                    }
                }
            } catch (error) {
                console.error("[Form Debug] Error during router update logic inside setTimeout:", error);
            }
            console.log('--- [Form Debug] updateVehicleUrl END (after setTimeout attempt) ---');
        }, 0);
    },

    async prefillFromUrl() {
      if (!this.$route?.query) {
          console.log("Router not ready or query missing during prefill attempt."); return;
      }
      const vehicleParam = this.$route.query.vehicle;
      if (!vehicleParam || typeof vehicleParam !== 'string' || !vehicleParam.includes('-')) {
        console.log("No valid vehicle param found in URL query for prefill.");
        // this.isPrefilling = false; // Ensure isPrefilling is reset if we exit early
        return;
      }
      console.log("Attempting prefill from vehicle param:", vehicleParam);
      // isPrefilling is already true from created()

      try {
        const idString = vehicleParam.split('-')[0];
        const ids = {};
        const regex = /(t|b|m|g|e)(\d+)/g;
        let match;
        while ((match = regex.exec(idString)) !== null) {
            const key = match[1];
            const value = match[2];
            if (key === 't') ids.vehicleTypeId = value;
            else if (key === 'b') ids.brandId = value;
            else if (key === 'm') ids.modelId = value;
            else if (key === 'g') ids.generationId = value;
            else if (key === 'e') ids.engineId = value;
        }

        if (!ids.vehicleTypeId || !ids.brandId || !ids.modelId || !ids.generationId || !ids.engineId) {
            throw new Error(`Failed to parse required IDs from param: ${idString}`);
        }
        console.log("Parsed IDs:", ids);

        if (this.vehicleTypes.length === 0) await this.getVehicleTypes();
        if (!this.vehicleTypes.some(vt => String(vt.id) === String(ids.vehicleTypeId))) throw new Error(`VehicleType ID ${ids.vehicleTypeId} invalid.`);
        this.form.vehicleType = String(ids.vehicleTypeId);
        await this.getBrands();

        if (!this.brands.some(b => String(b.id) === String(ids.brandId))) throw new Error(`Brand ID ${ids.brandId} invalid.`);
        this.form.brand = String(ids.brandId);
        await this.getModels();

        if (!this.models.some(m => String(m.id) === String(ids.modelId))) throw new Error(`Model ID ${ids.modelId} invalid.`);
        this.form.model = String(ids.modelId);
        await this.getGenerations();

        if (!this.generations.some(g => String(g.id) === String(ids.generationId))) throw new Error(`Generation ID ${ids.generationId} invalid.`);
        this.form.generation = String(ids.generationId);
        await this.getFuels(true); // Pass true to indicate preselecting

        this.form.fuel = null; // Default to 'All' for engines, specific fuel can be inferred if needed
        await this.getEngines();

        const engineToSelect = this.engines.find(e => String(e.id) === String(ids.engineId));
        if (!engineToSelect) throw new Error(`Engine ID ${ids.engineId} invalid or missing in fetched list.`);
        this.form.engine = String(engineToSelect.id);

        if (this.isFormValid) {
            console.log("Prefill complete, emitting getRemaps.");
            this.$nextTick(() => { this.$emit('getRemaps', { ...this.form }); });
        }
        console.log("Form state after successful prefill:", JSON.parse(JSON.stringify(this.form)));

      } catch (error) {
        console.error("Error during prefillFromUrl execution:", error.message || error);
        this.resetFieldsFrom('vehicleType');
        // No need to call updateVehicleUrl() here to clear,
        // as the created().finally block will handle it if prefill fails.
      }
      // isPrefilling is set to false in the created() hook's finally block
    },

     submitForm() {
      if (!this.isFormValid) {
        console.warn("Submit attempted but form is invalid.");
        return;
      }
      console.log("[Form Debug] Submit button clicked. Building URL and emitting getRemaps.");

      let vehicleParamValue = null;
      try {
          const typeId = this.form.vehicleType;
          const brandId = this.form.brand;
          const modelId = this.form.model;
          const generationId = this.form.generation;
          const engineId = this.form.engine;

          const selectedBrandObj = this.brands?.find(b => String(b.id) === String(brandId));
          const selectedModelObj = this.models?.find(m => String(m.id) === String(modelId));
          const selectedGenerationObj = this.generations?.find(g => String(g.id) === String(generationId));
          const selectedEngineObj = this.engines?.find(e => String(e.id) === String(engineId));

          if (typeId && brandId && modelId && generationId && engineId &&
              selectedBrandObj?.slug && selectedModelObj?.slug && selectedGenerationObj?.slug && selectedEngineObj?.slug)
          {
              const idSlug = `t${typeId}b${brandId}m${modelId}g${generationId}e${engineId}`;
              const humanReadableSlug = [
                  selectedBrandObj.slug, selectedModelObj.slug, selectedGenerationObj.slug, selectedEngineObj.slug
              ].join('_');
              vehicleParamValue = `${idSlug}-${humanReadableSlug}`;
              console.log("[Form Debug] Built vehicle param:", vehicleParamValue);
          } else {
              console.warn("[Form Debug] Could not build vehicle param - missing IDs or slugs.");
          }
      } catch (buildError) {
          console.error("[Form Debug] Error building vehicle param:", buildError);
      }

      this.updateVehicleUrl(vehicleParamValue);
      this.$emit('getRemaps', { ...this.form });
    },

    async selectGridItem(field, value) {
      if (this.isPrefilling) return; // Should not be reachable if v-if is correct, but good guard

      this.form[field] = String(value);

      // Trigger the corresponding onXChange method
      if (field === 'vehicleType') {
        await this.onVehicleTypeChange();
      } else if (field === 'brand') {
        await this.onBrandChange();
      } else if (field === 'model') {
        await this.onModelChange();
      } else if (field === 'generation') {
        this.form.fuel = null; // Reset fuel to ensure all engines are fetched for the grid
        // onGenerationChange will emit 'selection-changed-stale-remap' via resetFieldsFrom or updateVehicleUrl
        await this.onGenerationChange();
        } else if (field === 'engine') { // Engine selected from grid
          await this.onEngineChange(); // This will trigger submitForm
      }

    }
  }
}
</script>


<style lang="scss" scoped>
* {
  box-sizing: border-box;
}

.form-field-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(270px, 1fr));
  margin-left: calc(-10px / 2);
  margin-right: calc(-10px / 2);
  margin-bottom: -10px;
}

.field-group {
  padding-right: 5px;
  padding-left: 5px;
  margin-bottom: 10px;
  width: 100%;
  flex-wrap: wrap;
  align-items: center;
  position: relative;
  display: flex;

  label {
    padding-bottom: 5px;
    font-family: "PT Sans", sans-serif;
    font-size: 16px;
    font-weight: 600;
    // Use CSS variable for color
    color: var(--erc-form-text-color, #ffffff); // Added fallback
  }

  &__button {
    align-items: flex-end;
  }
}

.form-field {
  font-family: "Open Sans", sans-serif;
  font-size: 16px;
  font-weight: 300;
  flex-basis: 100%;
  max-width: 100%;
  // Use CSS variable for color
  color: var(--erc-select-text-color, #000); // Added fallback
  display: flex;
  position: relative;
  width: 100%;
}

.select-wrapper {
  &::before {
    content: url('data:image/svg+xml; utf8, <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" class="svg-inline--fa fa-caret-down" data-icon="caret-down" data-prefix="fas" viewBox="0 0 320 512"><path fill="currentColor" d="m310.6 246.6-127.1 128c-7.1 6.3-15.3 9.4-23.5 9.4s-16.38-3.125-22.63-9.375l-127.1-128C.2244 237.5-2.516 223.7 2.438 211.8S19.07 192 32 192h255.1c12.94 0 24.62 7.781 29.58 19.75s3.12 25.75-6.08 34.85z"/></svg>');
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 10px;
    pointer-events: none;
    text-shadow: 0 0 3px rgba(0, 0, 0, .3);
    width: 10px;
    // Consider theme adjustment for arrow color if needed
    // color: var(--erc-select-text-color, #000);
  }

  select {
    // Use CSS variables
    background-color: var(--erc-select-bg-color, #ffffff);
    border: 1px solid var(--erc-select-border-color, #818a91);
    color: inherit; // Inherits from .form-field

    border-radius: 9px;
    flex-basis: 100%;
    appearance: none;
    font-size: inherit;
    font-family: inherit;
    font-weight: inherit;
    font-style: inherit;
    text-transform: inherit;
    letter-spacing: inherit;
    line-height: inherit;
    width: 100%;
    max-width: 100%;
    flex-grow: 1;
    min-height: 40px;
    padding: 5px 20px 5px 14px;
  }
}

.field-group__button {
    align-items: flex-end;
    padding-top: 10px;
}

.submit-button {
  // Use CSS variables
  background-color: var(--erc-button-bg-color, #ffffff);
  color: var(--erc-button-text-color, var(--erc-color-oem, #00aeff)); // Use OEM color variable

  flex-basis: 100%;
  min-height: 40px;
  border: none;
  cursor: pointer;
  display: inline-block;
  line-height: 1;
  padding: 0 24px;
  fill: #fff;
  text-align: center;
  transition: all .3s;
  border-radius: 10px !important;
  font-weight: 700;

  &:hover:enabled,
  &:focus:enabled {
    // Use CSS variables
    background-color: var(--erc-button-hover-bg-color, #00699b);
    color: var(--erc-button-hover-text-color, #fff);
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
    // Consider theme variables for disabled state too
    background-color: #e3e3e3;
    color: #a7a7a7;
  }
}

</style>
